import { Button, HelperText, Input, InputRow, Select } from '@/ui';
import { ResetIcon } from '@/ui/icons';
import PropTypes from 'prop-types';
import { useEffect, useId, useState } from 'react';
import { z } from 'zod';

import bookTypes from '@/assets/bookTypes.json';
import './NuevaCausaForm.css';
import {
  FIXED_COMPETENCIA,
  OFFERED_LIBROS,
  emptyNuevaCausaValues,
} from './useNuevaCausaForm.js';

// The offered subset of the API's book catalog — see OFFERED_LIBROS.
const LIBROS = bookTypes.filter((b) => OFFERED_LIBROS.includes(b.value));

// Hover hints (native `title`) — shown on hover, no popover component.
const HINTS = {
  corte: 'Corte de Apelaciones a la que pertenece la causa.',
  tribunal: 'Tribunal de la causa. Depende de la corte seleccionada.',
  libro: 'Libro de la causa dentro del tribunal civil. C por defecto.',
  rol: 'Número de rol de la causa (solo dígitos).',
  anio: 'Año de la causa (4 dígitos).',
  submit: 'Suscribir la causa para hacerle seguimiento.',
  clear: 'Limpiar el formulario.',
};

const schema = z.object({
  competencia: z.string().min(1, 'Elige una competencia'),
  corte: z.string().min(1, 'Elige una corte'),
  tribunal: z.string().min(1, 'Elige un tribunal'),
  libro: z.string().min(1, 'Elige un libro'),
  rol: z
    .string()
    .min(1, 'Ingresa el número de rol')
    .regex(/^\d+$/, 'El rol debe ser un número'),
  anio: z
    .string()
    .length(4, 'El año debe tener 4 dígitos')
    .regex(/^\d{4}$/, 'Año inválido'),
  encargadoId: z.string().optional(),
});

function issuesToErrors(error) {
  const fe = {};
  error.issues.forEach((i) => {
    fe[i.path[0]] = i.message;
  });
  return fe;
}

// Field label with a hover hint (native title) — reachable on hover even on the
// disabled fields (a disabled <select> doesn't surface its own title).
function FieldLabel({ children, hint }) {
  return <span title={hint}>{children}</span>;
}

FieldLabel.propTypes = { children: PropTypes.node, hint: PropTypes.string };

// A field whose numeric "cod" sits up front as a muted, slightly smaller prefix
// (mirrors the phone field's "+56"); the native <select> beside it carries only
// the name. Disabled fields drop the chevron and read as static.
function CodSelect({
  cod,
  value,
  onChange,
  disabled,
  error,
  title,
  children,
  ...rest // id / aria-* injected by InputRow → forwarded to the <select>
}) {
  const cls = [
    'nc-codfield',
    disabled ? 'is-disabled' : '',
    error ? 'input-error' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls} title={title}>
      {cod ? <span className="nc-codfield__cod">cod {cod}</span> : null}
      <select
        className="nc-codfield__select"
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={error || undefined}
        {...rest}
      >
        {children}
      </select>
    </div>
  );
}

// InputRow labels the first element marked as a form control.
CodSelect.formControl = true;

CodSelect.propTypes = {
  cod: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.node,
};

export default function NuevaCausa({
  // The routes feed both catalogs from useNuevaCausaForm; fixtures for
  // stories/tests live in NuevaCausaForm.fixtures.js.
  cortes = [],
  tribunalesPorCorte = {},
  // Org members for the optional "Encargado" select.
  members = [],
  // Whether the requester may pick an encargado (case:assign — USER-0010).
  // Default hidden: roles below admin and solo orgs never see the field.
  canAssign = false,
  // Controlled mode: pass `values` + `onChange` so the state can live in a
  // parent (and survive the drawer unmounting). Omit both to run uncontrolled.
  values: valuesProp,
  onChange,
  onClear,
  initialValues,
  onSubmit,
  onCancel,
  apiError,
  isSubmitting = false,
  submitLabel = 'Suscribir',
  submittingLabel = 'Suscribiendo…',
  submitDisabled = false,
  validateFutureYear = false,
}) {
  const controlled = valuesProp !== undefined && typeof onChange === 'function';
  const [internalValues, setInternalValues] = useState(() => ({
    ...emptyNuevaCausaValues(),
    ...initialValues,
  }));
  const values = controlled ? valuesProp : internalValues;

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const anioErrorId = `${useId()}-anio-error`;

  function set(field, value) {
    if (controlled) {
      onChange(field, value);
    } else {
      setInternalValues((prev) => {
        const next = { ...prev, [field]: value };
        if (field === 'corte') next.tribunal = '';
        return next;
      });
    }
  }

  // Re-validate live once the user has attempted a submit, whichever side owns
  // the values.
  useEffect(() => {
    if (!submitted) return;
    const result = schema.safeParse(values);
    setErrors(
      result.success
        ? validateFutureYear && Number(values.anio) > new Date().getFullYear()
          ? { anio: 'El año no puede ser futuro' }
          : {}
        : issuesToErrors(result.error),
    );
  }, [values, submitted, validateFutureYear]);

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    const result = schema.safeParse(values);
    if (!result.success) {
      setErrors(issuesToErrors(result.error));
      return;
    }
    if (submitDisabled || isSubmitting) return;
    if (validateFutureYear && Number(values.anio) > new Date().getFullYear()) {
      setErrors({ anio: 'El año no puede ser futuro' });
      return;
    }
    setErrors({});
    if (onSubmit) onSubmit(result.data);
  }

  function handleClear() {
    setSubmitted(false);
    setErrors({});
    if (controlled) onClear?.();
    else setInternalValues(emptyNuevaCausaValues());
  }

  const tribunales = values.corte ? tribunalesPorCorte[values.corte] || [] : [];

  return (
    <form onSubmit={handleSubmit} noValidate>
      <InputRow label={<FieldLabel hint={HINTS.corte}>Corte</FieldLabel>}>
        <CodSelect
          cod={values.corte || ''}
          value={values.corte}
          onChange={(e) => set('corte', e.target.value)}
          error={Boolean(errors.corte)}
          title={HINTS.corte}
        >
          <option value="">Seleccionar…</option>
          {cortes.map((c) => (
            <option key={c.cod} value={c.cod}>
              {c.label}
            </option>
          ))}
        </CodSelect>
        {errors.corte && <HelperText kind="err">{errors.corte}</HelperText>}
      </InputRow>

      <InputRow label={<FieldLabel hint={HINTS.tribunal}>Tribunal</FieldLabel>}>
        <CodSelect
          cod={values.tribunal || ''}
          value={values.tribunal}
          onChange={(e) => set('tribunal', e.target.value)}
          disabled={!values.corte}
          error={Boolean(errors.tribunal)}
          title={HINTS.tribunal}
        >
          <option value="">
            {values.corte ? 'Seleccionar…' : 'Elige una corte primero'}
          </option>
          {tribunales.map((t) => (
            <option key={t.cod} value={t.cod}>
              {t.label}
            </option>
          ))}
        </CodSelect>
        {errors.tribunal && (
          <HelperText kind="err">{errors.tribunal}</HelperText>
        )}
      </InputRow>

      {/* Fixed, not user-editable: shown as derived text, like Corte/Tribunal
          in NuevaDiligenciaForm's locked-causa view. */}
      <InputRow label="Competencia">
        <div className="nc-fixed">{FIXED_COMPETENCIA.label}</div>
      </InputRow>

      <InputRow label={<FieldLabel hint={HINTS.libro}>Libro</FieldLabel>}>
        <CodSelect
          value={values.libro}
          onChange={(e) => set('libro', e.target.value)}
          error={Boolean(errors.libro)}
          title={HINTS.libro}
        >
          {LIBROS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </CodSelect>
        {errors.libro && <HelperText kind="err">{errors.libro}</HelperText>}
      </InputRow>

      <InputRow label={<FieldLabel hint={HINTS.rol}>Rol / Año</FieldLabel>}>
        <div className="nc-rol-row">
          <Input
            variant="mono"
            className={errors.rol ? 'focused input-error' : 'focused'}
            placeholder="12345"
            value={values.rol}
            onChange={(e) => set('rol', e.target.value)}
            style={{ flex: 1 }}
            title={HINTS.rol}
          />
          <Input
            variant="mono"
            value={values.anio}
            onChange={(e) => set('anio', e.target.value)}
            style={{ width: 90 }}
            className={errors.anio ? 'input-error' : ''}
            title={HINTS.anio}
            aria-label="Año"
            aria-describedby={errors.anio ? anioErrorId : undefined}
          />
        </div>
        {/* InputRow wires the rol error to the rol input; the year error brings
            its own id and describes the year input. */}
        {errors.rol && <HelperText kind="err">{errors.rol}</HelperText>}
        {errors.anio && (
          <HelperText id={anioErrorId} kind="err">
            {errors.anio}
          </HelperText>
        )}
      </InputRow>

      {canAssign && (
        <InputRow label="Encargado (opcional)">
          <Select
            value={values.encargadoId}
            onChange={(e) => set('encargadoId', e.target.value)}
          >
            <option value="">Sin asignar</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </Select>
        </InputRow>
      )}

      <HelperText>
        El sistema validará automáticamente el rol contra el Poder Judicial al
        suscribir.
      </HelperText>

      {apiError && <HelperText kind="err">{apiError}</HelperText>}

      <div className="nc-actions">
        <button
          type="button"
          className="nc-clear"
          onClick={handleClear}
          disabled={isSubmitting}
          title={HINTS.clear}
        >
          <ResetIcon />
          Limpiar
        </button>
        <Button
          variant="ghost"
          size="md"
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          size="md"
          type="submit"
          disabled={isSubmitting || submitDisabled}
          title={HINTS.submit}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}

const optionShape = PropTypes.shape({
  cod: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

NuevaCausa.propTypes = {
  /** Cortes as `{ cod, label }`; the cod is shown as a muted prefix. */
  cortes: PropTypes.arrayOf(optionShape),
  /** `{ [corteCod]: [{ cod, label }] }` — tribunals for the selected corte. */
  tribunalesPorCorte: PropTypes.objectOf(PropTypes.arrayOf(optionShape)),
  /** Org members for the optional "Encargado" select. */
  members: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, nombre: PropTypes.string }),
  ),
  /** Shows the "Encargado" field (requester holds case:assign). */
  canAssign: PropTypes.bool,
  /** Controlled values (pair with `onChange`). Omit for uncontrolled. */
  values: PropTypes.object,
  onChange: PropTypes.func,
  /** Clear handler for controlled mode (the clear icon). */
  onClear: PropTypes.func,
  initialValues: PropTypes.shape({
    competencia: PropTypes.string,
    corte: PropTypes.string,
    tribunal: PropTypes.string,
    libro: PropTypes.string,
    rol: PropTypes.string,
    anio: PropTypes.string,
    encargadoId: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  apiError: PropTypes.string,
  isSubmitting: PropTypes.bool,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
  submitDisabled: PropTypes.bool,
  validateFutureYear: PropTypes.bool,
};
